/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Phase A R2 — Mixer FX rack UI panel.
 *
 * Renders 5 effects (Echo, Reverb, Delay, LPF, HPF). Each strip has:
 *   - Enable toggle (button)
 *   - "Send from" select (A | B | C | D | Master)
 *   - Wet (dry/wet) knob
 *   - Param knob (time / size / cutoff)
 *
 * Controls drive the engine's `fxRack` via the supplied callbacks so the
 * same component works against `DjEngine` (inline) and `RemoteEngine`
 * (pop-out via BroadcastChannel).
 */
import { useState } from "react";
import { ChevronDown, ChevronUp, Zap } from "lucide-react";

const FX_DEFS = [
  { name: "echo",   label: "Echo",    params: [{ key: "time",   label: "TIME",   min: 0.05, max: 2.0, step: 0.01 }] },
  { name: "reverb", label: "Reverb",  params: [{ key: "size",   label: "SIZE",   min: 0,    max: 1,   step: 0.01 }] },
  { name: "delay",  label: "Delay",   params: [{ key: "time",   label: "TIME",   min: 0.05, max: 2.0, step: 0.01 }] },
  { name: "lpf",    label: "LPF",     params: [{ key: "cutoff", label: "CUT",    min: 80,   max: 22050, step: 10, scale: "log" }] },
  { name: "hpf",    label: "HPF",     params: [{ key: "cutoff", label: "CUT",    min: 40,   max: 22050, step: 10, scale: "log" }] },
];

export default function FxRack({ fxState, layoutMode, onSetEnabled, onSetParam, onSetSendFrom }) {
  const [collapsed, setCollapsed] = useState(false);
  const sources = layoutMode === "pro4"
    ? ["A", "B", "C", "D", "master"]
    : ["A", "B", "master"];
  return (
    <div className="rounded-xl border border-white/10 bg-[var(--sr-surface)] mt-2" data-testid="fx-rack" data-sr-fxrack>
      <button onClick={() => setCollapsed((v) => !v)}
              className="w-full px-3 py-1.5 flex items-center justify-between text-[10px] uppercase tracking-[0.25em] font-bold text-white/70 hover:text-white"
              data-testid="fx-rack-toggle">
        <span className="flex items-center gap-1.5">
          <Zap className="w-3 h-3" />
          FX Rack · {Object.values(fxState || {}).filter((f) => f?.enabled).length}/5 active
        </span>
        {collapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
      </button>
      {!collapsed && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2 px-2 pb-2">
          {FX_DEFS.map((def) => {
            const s = fxState?.[def.name] || { enabled: false, sendFrom: "master", wetLevel: 0.5 };
            const param = def.params[0];
            return (
              <div key={def.name} className="bg-[var(--sr-raised)] border border-white/10 rounded-lg p-2 flex flex-col gap-1.5"
                   data-testid={`dj-fx-${def.name}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--sr-text)]">{def.label}</span>
                  <button onClick={() => onSetEnabled?.(def.name, !s.enabled)}
                          className={`w-6 h-3.5 rounded-full transition-colors ${s.enabled ? "" : "bg-white/15"}`}
                          style={s.enabled ? { background: "var(--sr-magenta)" } : undefined}
                          data-testid={`dj-fx-${def.name}-enable`}
                          aria-pressed={s.enabled}>
                    <span className={`block w-3 h-3 rounded-full bg-white transition-transform translate-y-0.25 ${s.enabled ? "translate-x-2.5" : "translate-x-0.25"}`} />
                  </button>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] uppercase tracking-[0.15em] text-white/45 w-8">Send</span>
                  <select value={s.sendFrom} onChange={(e) => onSetSendFrom?.(def.name, e.target.value)}
                          className="flex-1 text-[10px] bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-white"
                          data-testid={`dj-fx-${def.name}-send`}>
                    {sources.map((src) => <option key={src} value={src}>{src.toUpperCase()}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <SmallKnob label="WET" value={s.wetLevel ?? 0.5} min={0} max={1}
                             onChange={(v) => onSetParam?.(def.name, "wet", v)}
                             testid={`dj-fx-${def.name}-wet`} />
                  <SmallKnob label={param.label} value={s[param.key] ?? param.min}
                             min={param.min} max={param.max}
                             scale={param.scale}
                             onChange={(v) => onSetParam?.(def.name, param.key, v)}
                             testid={`dj-fx-${def.name}-${param.key}`} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SmallKnob({ label, value, min, max, scale, onChange, testid }) {
  const startY = (e) => {
    const y0 = e.clientY;
    let v0;
    if (scale === "log") {
      const lmin = Math.log(min); const lmax = Math.log(max);
      v0 = (Math.log(Math.max(min, value)) - lmin) / (lmax - lmin);
    } else {
      v0 = (value - min) / (max - min);
    }
    const onMove = (ev) => {
      const dy = y0 - ev.clientY;
      let t = Math.max(0, Math.min(1, v0 + dy / 120));
      let out;
      if (scale === "log") {
        const lmin = Math.log(min); const lmax = Math.log(max);
        out = Math.exp(lmin + t * (lmax - lmin));
      } else {
        out = min + t * (max - min);
      }
      onChange?.(out);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  let pct;
  if (scale === "log") {
    pct = (Math.log(Math.max(min, value)) - Math.log(min)) / (Math.log(max) - Math.log(min));
  } else {
    pct = (value - min) / (max - min);
  }
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div
        className="w-7 h-7 rounded-full bg-white/10 border border-white/20 flex items-center justify-center cursor-ns-resize select-none relative"
        onPointerDown={startY}
        data-testid={testid}
        title={`${label}: ${typeof value === "number" ? value.toFixed(2) : value}`}>
        <span className="absolute top-0.5 w-px h-2"
              style={{ background: "var(--sr-magenta)", transform: `rotate(${-135 + pct * 270}deg)`, transformOrigin: "50% 12px" }} />
      </div>
      <span className="text-[8px] uppercase tracking-[0.18em] text-white/40 leading-none">{label}</span>
    </div>
  );
}
